import { InputLabel, MenuItem, SelectChangeEvent } from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import { ItemVM } from 'shell/view-models/item';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { ListItemType } from 'shell/models/item-type';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';
import { PmiSelect, PmiSelectProps } from 'shell/components/shared/pmi-select';

interface ContinentListComponentProps<T> extends PmiSelectProps<T> {
  disabled?: boolean;
  value: string;
  onChange: (event: SelectChangeEvent<string>) => void;
  error?: boolean;
}

const ContinentListComponent = <T,>({
  disabled,
  onChange,
  value,
  error,
  helperText,
}: ContinentListComponentProps<T>): React.ReactElement => {
  const {
    data,
    error: errorlist,
    get: getContinentListComponent,
  } = useHttpFetch<HttpResponseList<ListItemType>>(
    `${__MUNICIPALITY_API_URL__}/geo/territorio/continenti`,
    {
      data: {
        payload: {
          list: [],
        },
      },
    }
  );

  const list: Array<ItemVM> = useMemo(() => {
    if (!!errorlist || !data?.payload) {
      return [];
    }
    return data?.payload.list.map((item) => ({
      id: item.key,
      value: item.descrizione,
    }));
  }, [data, errorlist]);

  useEffect(() => {
    getContinentListComponent();
  }, []);

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} disabled={disabled} id="continent-label">
        Continente *
      </InputLabel>
      <PmiSelect
        labelId="continent-label"
        id="continent"
        name="continent"
        value={value}
        label="Continente *"
        onChange={onChange}
        disabled={disabled}
        error={error}
        helperText={helperText}
      >
        <MenuItem value="">
          <em>Nessuno</em>
        </MenuItem>
        {list.map((item: ItemVM) => (
          <MenuItem key={item.id} value={item.id}>
            {item.value}
          </MenuItem>
        ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ContinentListComponent.displayName = 'Continent item component';

const ContinentList = memo(ContinentListComponent);

export default ContinentList as typeof ContinentListComponent;
